import { getCentroCustoAtual } from '@acme/common'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { DescontoDSRItem, NivelDSR } from './niveis-agrupamento'

dayjs.extend(utc)

export interface LinhaExplodidaDSR extends DescontoDSRItem {
    _centroCustoKey: string
    _centroCustoNome: string
    _localKey: string
    _localNome: string
    _funcionarioKey: string
    _funcionarioNome: string
    _dataKey: string
    _dataLabel: string
}

/**
 * Normaliza as linhas de Desconto DSR com chaves físicas de agrupamento.
 * Mapeamento 1:1 estrito sem fan-out (não duplica linhas de DSR).
 * Chaves físicas isoladas por empresa (${empresaId}_${id}) para evitar colisões.
 * Centro de Custo é resolvido deterministamente pela data do evento DSR (linha.data).
 */
export function explodirLinhas(
    linhas: DescontoDSRItem[],
    _niveisAtivos?: Set<NivelDSR>,
): LinhaExplodidaDSR[] {
    return (linhas ?? []).map(linha => {
        const funcionario = linha.Funcionario
        const empresaId =
            (linha as any).empresaId ??
            funcionario?.empresaId ??
            (funcionario as any)?.Empresa?.id ??
            0

        const vinculosCC = (
            Array.isArray(funcionario?.FuncionarioCentroCusto)
                ? funcionario.FuncionarioCentroCusto
                : [funcionario?.FuncionarioCentroCusto].filter(Boolean)
        ) as Array<{
            centroCustoId?: number
            dtInicial?: string | Date
            dtFinal?: string | Date | null
            CentroCusto?: {
                id?: number
                codigo?: string
                nomeCentroCusto?: string
                predecessorId?: number
            }
        }>

        const dataReferencia = linha.data ? dayjs.utc(linha.data) : dayjs.utc()

        const vinculosSeguros = vinculosCC.map(v => {
            const centroCusto = v.CentroCusto
                ? {
                      id: v.CentroCusto.id ?? v.centroCustoId ?? 0,
                      codigo: v.CentroCusto.codigo ?? '',
                      nomeCentroCusto: v.CentroCusto.nomeCentroCusto ?? '',
                      predecessorId: (v.CentroCusto as any).predecessorId ?? 0,
                  }
                : undefined

            return {
                centroCustoId: v.centroCustoId ?? v.CentroCusto?.id ?? 0,
                dtInicial: v.dtInicial ?? '',
                dtFinal: v.dtFinal,
                CentroCusto: centroCusto,
            }
        })

        const ativosCC = funcionario?.FuncionarioCentroCusto
            ? getCentroCustoAtual(
                  { FuncionarioCentroCusto: vinculosSeguros as any },
                  dataReferencia,
              )
            : []

        const ativosFiltrados = ativosCC.filter(
            cc =>
                !cc.dtInicial ||
                !dayjs.utc(cc.dtInicial).isAfter(dataReferencia, 'day'),
        )

        const cc =
            (ativosFiltrados[0] ?? ativosCC[0])?.CentroCusto ??
            (vinculosCC[0] as any)?.CentroCusto
        const ccId =
            cc?.id ??
            ativosFiltrados[0]?.centroCustoId ??
            ativosCC[0]?.centroCustoId ??
            vinculosCC[0]?.centroCustoId ??
            (linha as any).centroCustoId ??
            0
        const ccNome =
            cc?.nomeCentroCusto ??
            (linha as any).centroCustoNome ??
            (ccId ? `Centro de custo ${ccId}` : 'Sem centro de custo')

        const vinculosLocais = (
            Array.isArray(funcionario?.FuncionarioLocais)
                ? funcionario.FuncionarioLocais
                : [funcionario?.FuncionarioLocais].filter(Boolean)
        ) as Array<{
            localId?: number
            Local?: { id?: number; nomeLocal?: string }
        }>
        const loc = vinculosLocais[0]?.Local ?? (linha as any).Local
        const locId = loc?.id ?? (linha as any).localId ?? 0
        const locNome =
            loc?.nomeLocal ??
            (linha as any).localNome ??
            (locId ? `Local ${locId}` : 'Sem local')

        const funcId =
            linha.funcionarioId ?? funcionario?.id ?? (linha as any).id ?? 0
        const funcNome =
            funcionario?.nome ??
            (linha as any).funcionarioNome ??
            (funcId ? `Funcionário ${funcId}` : '')

        const dataLabel = linha.data
            ? dayjs(linha.data).format('DD/MM/YYYY - dddd')
            : ''

        return {
            ...linha,
            _centroCustoKey: `${empresaId}_${ccId}`,
            _centroCustoNome: ccNome,
            _localKey: `${empresaId}_${locId}`,
            _localNome: locNome,
            _funcionarioKey: `${empresaId}_${funcId}`,
            _funcionarioNome: funcNome,
            _dataKey: String(linha.data || ''),
            _dataLabel: dataLabel,
        }
    })
}
